import { Box, Chip, FormControl, MenuItem, Select } from "@mui/material";
import { Button, Input, Label } from "abs-ui-components";
import { MDBCol, MDBRow } from "mdbreact";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { callApi } from "../../../../../services/MonitoringPlanService";
import "./FeuB2CompanyInformation.scss";
import { AUTOSAVE_SUCCESS_MESSAGE, REQUIRED_FIELDS_FOR_B2 } from "../../FeuConstants";
import { forwardRef, useEffect, useRef } from "react";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const FeuB2CompanyInformation = forwardRef(({ b2CompanyInfo, refetchMonitoringData, b2CompanyInfoValues, vesselMstId }, ref) => {
	const {
		control,
		handleSubmit,
		formState: { errors, isDirty },
		trigger,
		getValues,
		reset,
	} = useFormContext();

	const manualSaveTriggered = useRef(false);
	const location = useLocation();
	const { mode } = location.state || {};
	const companyNatureOptions = b2CompanyInfoValues || [];
	const handleBlur = async (fieldName) => {
		await trigger(fieldName);
	};

	useEffect(() => {
		if (b2CompanyInfo) {
			reset({
				companyName: b2CompanyInfo?.companyName ?? "",
				companyNatures: Array.isArray(b2CompanyInfo?.companyNatures) ? b2CompanyInfo.companyNatures : [],
				companyImoNumber: b2CompanyInfo?.companyImoNumber ?? "",
				countryOfRegistration: b2CompanyInfo?.countryOfRegistration ?? "",
				taxNumber: b2CompanyInfo?.taxNumber ?? "",
				contactPerson: b2CompanyInfo?.contactPerson ?? "",
				businessAddress: b2CompanyInfo?.businessAddress ?? "",
				city: b2CompanyInfo?.city ?? "",
				stateProvinceRegion: b2CompanyInfo?.stateProvinceRegion ?? "",
				zipCode: b2CompanyInfo?.zipCode ?? "",
				businessTelephone: b2CompanyInfo?.businessTelephone ?? "",
				businessEmail: b2CompanyInfo?.businessEmail ?? "",
				country: b2CompanyInfo?.country ?? "",
			});
		}
	}, [b2CompanyInfo, reset]);

	AutoSave(ref, REQUIRED_FIELDS_FOR_B2, async () => {
		if (!isDirty) return;
		const formValues = getValues();
		await onSubmit(formValues, true);
	});
	const onSubmit = async (data, isAuto = false) => {
		const payload = {
			companyName: data?.companyName || "",
			companyNatures: data?.companyNatures || [],
			companyImoNumber: data?.companyImoNumber || "",
			countryOfRegistration: data?.countryOfRegistration || "",
			taxNumber: data?.taxNumber || "",
			contactPerson: data?.contactPerson || "",
			businessAddress: data?.businessAddress || "",
			city: data?.city || "",
			stateProvinceRegion: data?.stateProvinceRegion || "",
			zipCode: data?.zipCode || "",
			businessTelephone: data?.businessTelephone || "",
			businessEmail: data?.businessEmail || "",
			country: data?.country || "",
		};

		try {
			const response = await callApi({
				method: "POST",
				url: API_ROUTES.B2(vesselMstId),
				data: payload,
			});

			if (response?.isSuccess) {
				emitToasterMsg("success", "Table B.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
				reset(getValues());
			} else {
				emitToasterMsg("error", "Table B.2", response?.errorMessage || "Save failed");
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.2", error.errorMessage);
		}
	};

	return (
		<div className="form-section-b2 mt-5">
			<MDBRow className="align-items-center mb-2">
				<MDBCol md="4">
					<Label name="Name of the company" bold required />

					<Controller
						name="companyName"
						control={control}
						rules={{ required: "Field is required" }}
						defaultValue={b2CompanyInfo?.companyName ?? ""}
						render={({ field }) => (
							<>
								<Input
									id="nameOFTheCompany"
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
									onBlur={() => handleBlur(field.name)}
									className="form-input"
									required
								/>
								<div className="error-msg-b2ship">{errors?.companyName?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
				</MDBCol>
				<MDBCol md="4">
					<Label name="Nature of the company" bold required />
					<Controller
						name="companyNatures"
						control={control}
						rules={{ required: "Field is required" }}
						defaultValue={Array.isArray(b2CompanyInfo?.companyNatures) ? b2CompanyInfo.companyNatures : []}
						render={({ field }) => (
							<>
								<FormControl fullWidth size="small">
									<Select
										className="autocomplete-input"
										labelId="company-nature-label"
										multiple
										value={field.value || []}
										onChange={field.onChange}
										renderValue={(selected) => (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{selected?.map((val) => {
													const item = companyNatureOptions.find((opt) => opt?.code === val);
													return (
														<Chip
															key={val}
															label={item?.name || val}
															onMouseDown={(event) => {
																event.stopPropagation();
															}}
															onDelete={() => {
																const newValue = selected.filter((v) => v !== val);
																field.onChange(newValue);
															}}
														/>
													);
												})}
											</Box>
										)}
										MenuProps={{
											disableScrollLock: true,
											PaperProps: {
												sx: {
													maxHeight: 250,
													mt: 1,
													boxShadow: 3,
												},
											},
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											getContentAnchorEl: null,
										}}
									>
										{companyNatureOptions?.length === 0 ? (
											<MenuItem disabled sx={{ justifyContent: "center", fontStyle: "italic" }}>
												No options
											</MenuItem>
										) : (
											companyNatureOptions?.map((option) => (
												<MenuItem key={option.code} value={option?.code}>
													{option.name}
												</MenuItem>
											))
										)}
									</Select>
								</FormControl>
								<div className="error-msg-b2ship">{errors?.companyNatures?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="IMO unique company and registered owner identification number of the company" bold />
					<Controller
						name="companyImoNumber"
						control={control}
						defaultValue={b2CompanyInfo?.companyImoNumber ?? ""}
						render={({ field }) => (
							<>
								<Input
									id="company_imo_number"
									className="form-input"
									onBlur={() => handleBlur(field.name)}
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
								/>
								<div className="error-msg-b2ship">{"" || <>&nbsp;</>}</div>
							</>
						)}
					/>
				</MDBCol>
			</MDBRow>

			<MDBRow className="align-items-center mb-3">
				<MDBCol md="4">
					<Label name="Country of registration of the company" bold />

					<Controller
						name="countryOfRegistration"
						control={control}
						defaultValue={b2CompanyInfo?.countryOfRegistration ?? ""}
						render={({ field }) => (
							<Input
								id="country_of_registration"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
				<MDBCol md="4">
					<Label name="Company tax number (voluntary)" bold />
					<Controller
						name="taxNumber"
						control={control}
						defaultValue={b2CompanyInfo?.taxNumber ?? ""}
						render={({ field }) => (
							<Input
								id="tax_number"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								maxLength={255}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
				<MDBCol md="4">
					<Label name="Contact person" bold />

					<Controller
						name="contactPerson"
						control={control}
						defaultValue={b2CompanyInfo?.contactPerson ?? ""}
						render={({ field }) => (
							<Input
								id="contact_person"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								maxLength={255}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="align-items-center mb-3">
				<MDBCol md="4">
					<Label name="Address" bold />
					<Controller
						name="businessAddress"
						control={control}
						defaultValue={b2CompanyInfo?.businessAddress ?? ""}
						render={({ field }) => (
							<Input
								id="address"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								maxLength={255}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
				<MDBCol md="4">
					<Label name="City" bold />
					<Controller
						name="city"
						control={control}
						defaultValue={b2CompanyInfo?.city ?? ""}
						render={({ field }) => (
							<Input
								id="city"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								value={field.value}
								maxLength={255}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
				<MDBCol md="4">
					<Label name="State/Province/Region" bold />
					<Controller
						name="stateProvinceRegion"
						control={control}
						defaultValue={b2CompanyInfo?.stateProvinceRegion ?? ""}
						render={({ field }) => (
							<Input
								id="state_province"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								maxLength={255}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>
			</MDBRow>

			<MDBRow className="align-items-center mb-3">
				<MDBCol md="4">
					<Label name="Postcode/ZIP" bold />
					<Controller
						name="zipCode"
						control={control}
						defaultValue={b2CompanyInfo?.zipCode ?? ""}
						render={({ field }) => (
							<Input
								id="zip_code"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								value={field.value}
								maxLength={255}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Country" bold />
					<Controller
						name="country"
						control={control}
						defaultValue={b2CompanyInfo?.country ?? ""}
						render={({ field }) => (
							<Input
								id="country"
								className="form-input"
								onBlur={() => handleBlur(field.name)}
								autoFocus={false}
								maxLength={255}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Telephone number" bold />
					<Controller
						name="businessTelephone"
						control={control}
						defaultValue={b2CompanyInfo?.businessTelephone ?? ""}
						rules={{
							validate: (value) => {
								if (!value) return true; // allow empty
								return /^\+?[0-9]{7,15}$/.test(value) ? true : "Enter a valid telephone number (7–15 digits, optional +).";
							},
						}}
						render={({ field }) => (
							<>
								<Input
									id="telephone"
									className="form-input"
									onBlur={() => handleBlur(field.name)}
									autoFocus={false}
									maxLength={16} // enough for + and 15 digits
									value={field.value}
									onChange={field.onChange}
								/>
								<div className="error-msg-b1ship">{errors?.businessTelephone?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
				</MDBCol>
			</MDBRow>
			<MDBRow className="align-items-center mb-3">
				<MDBCol md="4">
					<Label name="Email address" bold />
					<Controller
						name="businessEmail"
						control={control}
						defaultValue={b2CompanyInfo?.businessEmail ?? ""}
						rules={{
							validate: (value) => {
								if (!value) return true; // allow empty
								return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value) ? true : "Please enter a valid email address.";
							},
						}}
						render={({ field }) => (
							<>
								<Input
									id="email"
									className="form-input"
									onBlur={() => handleBlur(field.name)}
									autoFocus={false}
									maxLength={255}
									value={field.value}
									onChange={field.onChange}
								/>
								<div className="error-msg-b1ship">{errors?.businessEmail?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
				</MDBCol>
			</MDBRow>

			<div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
				<Button
					disabled={!isDirty}
					onClick={async () => {
						const isValid = await trigger([...REQUIRED_FIELDS_FOR_B2,"businessEmail","businessTelephone"]);
						if (!isValid) return;

						manualSaveTriggered.current = true;
						await onSubmit(getValues(), false);
					}}
				>
					Save
				</Button>
			</div>
		</div>
	);
});

export default FeuB2CompanyInformation;
